/**
 * CÓMO SE LLAMA UN CUADRO EN PANTALLA: «B · Dobles mixto».
 *
 * POR QUÉ EXISTE. Esto estaba escrito a mano en DIECISÉIS lugares, siempre
 * igual: el nivel, un punto medio, y el sexo. Con dos sexos funcionaba. Los
 * dobles lo rompieron de dos maneras a la vez, y ninguna avisa:
 *
 *  · el singles B masculino y el dobles B masculino decían los DOS «B ·
 *    Masculino» — dos cuadros distintos con la misma etiqueta, en la misma
 *    pantalla, uno encima del otro;
 *  · y el mixto salía «B · sex.MIXED», la llave cruda, porque `MIXED` no es
 *    un sexo de persona y esa traducción no existe ni debería.
 *
 * Lo segundo se vio en una captura. Lo primero no se ve: se lee mal y ya.
 *
 * RECIBE EL OBJETO Y NO LOS CAMPOS SUELTOS, por lo mismo que `categoryKey`: el
 * día que un cuadro tenga un campo más, quien pase la categoría entera lo
 * hereda solo y quien arme un objeto a mano deja de compilar.
 */
import type { TFunction } from 'i18next';
import { CATEGORY_LEVELS } from '@tennisup/contracts';
import type { CategoryDiscipline, CategoryLevel, CategorySex, Sex } from '@tennisup/contracts';

export function categoryName(
  t: TFunction,
  category: { level: CategoryLevel; sex: CategorySex; discipline: CategoryDiscipline },
): string {
  return `${t(`categoryLevel.${category.level}`)} · ${t(`categoryGroup.${category.discipline}_${category.sex}`)}`;
}

/**
 * EL NOMBRE DE UNA TABLA DE RANKING: «B · Masculino», sin la disciplina.
 *
 * ES EL HERMANO CORTO DE `categoryName`, y existe porque los rankings tienen
 * una disciplina sola. Hoy todos son de singles, así que «B · Singles
 * masculino» gasta una palabra en distinguir de nada; donde conviven las dos
 * —las categorías de torneo— se sigue usando `categoryName`, que sí la dice.
 *
 * VIVE ACÁ Y NO SUELTO EN LA PANTALLA porque «nivel · sexo» se arma en un
 * solo archivo en todo el repo, y hay una prueba que lo hace cumplir
 * (`llave-de-categoria.spec.ts`). Escrito a mano en cada lugar, el separador
 * y el orden se van separando hasta que la misma categoría se lee distinta
 * en dos pantallas.
 */
export function rankingName(
  t: TFunction,
  ranking: { level: CategoryLevel; sex: CategorySex },
): string {
  return `${t(`categoryLevel.${ranking.level}`)} · ${t(`sex.${ranking.sex}`)}`;
}

/** El grupo solo —«Dobles mixto»—, para donde el nivel ya se dijo aparte. */
export function categoryGroupName(
  t: TFunction,
  category: { sex: CategorySex; discipline: CategoryDiscipline },
): string {
  return t(`categoryGroup.${category.discipline}_${category.sex}`);
}

/**
 * EL SEXO DE UNA PERSONA, escrito: «Masculino».
 *
 * Existe para poder DISTINGUIRSE de `categoryName` a simple vista, y no por
 * comodidad: las dos cosas se decían igual —`t(`sex.${algo}`)`— y así no había
 * manera de mirar una línea y saber si hablaba de una persona o de un cuadro.
 * Ahora cada pregunta tiene su función, y la prueba que prohíbe armar el
 * nombre de un cuadro a mano ya no tropieza con las líneas que hablan de
 * gente.
 *
 * Toma `Sex` y no `CategorySex`: no hay personas mixtas, y el tipo lo dice.
 */
export function personSexName(t: TFunction, sex: Sex): string {
  return t(`sex.${sex}`);
}


/**
 * LOS NIVELES QUE UN CLUB CORRE PARA UN SEXO, CADA UNO UNA SOLA VEZ.
 *
 * Una categoría del club es NIVEL × SEXO × DISCIPLINA, pero la MEMBRESÍA es
 * solo el NIVEL —`updateMemberLevel(communityId, userId, level)` guarda eso y
 * nada más—. Así que donde se elige «en qué categoría entro» la disciplina no
 * pinta nada, y dejarla pintar es lo que rompió la pantalla: un club que corre
 * «B masculino singles» y «B masculino dobles» son dos categorías y UN solo
 * nivel B, y salían dos botones «B» idénticos. Javier los vio en producción:
 * «Open, A+, A, A, B, B, C, C».
 *
 * Y no era solo de más: los dos botones tenían el mismo `value`, o sea que
 * marcar uno marcaba el otro y no se entendía cuál quedaba elegido.
 *
 * EL ORDEN LO PONE `CATEGORY_LEVELS` y no el servidor. La escalera va de Open
 * a C2 y así se lee; ordenar por como vengan dejaría la lista en distinto
 * orden en cada club, según en qué orden se hayan creado las categorías.
 */
export function clubLevels(
  categories: readonly { level: CategoryLevel; sex: CategorySex }[],
  sex: Sex,
  exclude: CategoryLevel | null = null,
): CategoryLevel[] {
  const suyos = new Set(categories.filter((c) => c.sex === sex).map((c) => c.level));
  return CATEGORY_LEVELS.filter((nivel) => suyos.has(nivel) && nivel !== exclude);
}
